if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const bundleContext = this.getBundleContext();
        if (bundleContext?.isBundleOffer && Array.isArray(bundleContext.mainItems) && bundleContext.mainItems.length) {
          this.handleBundleAdd(bundleContext, evt);
          return;
        }
        const formData = new FormData(this.form);
        const forcedBundleQty = bundleContext?.quantity || this.form.dataset.bundleQty;
        const forcedBundleVariantId = bundleContext?.variantId || this.form.dataset.bundleVariantId;
        if (forcedBundleQty) formData.set('quantity', forcedBundleQty);
        if (forcedBundleVariantId) formData.set('id', forcedBundleVariantId);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const addGiftItems = () => {
              const rawGiftItems = bundleContext?.giftItems?.length
                ? JSON.stringify(bundleContext.giftItems)
                : this.form.dataset.bundleGiftItems;
              if (!rawGiftItems) return Promise.resolve(false);
              let parsedGiftItems = [];
              try {
                parsedGiftItems = JSON.parse(rawGiftItems);
              } catch (error) {
                return Promise.resolve(false);
              }
              if (!Array.isArray(parsedGiftItems) || parsedGiftItems.length === 0) return Promise.resolve(false);
              const aggregatedGiftItems = Array.from(
                parsedGiftItems
                  .filter((item) => item && Number(item.id) > 0)
                  .reduce((acc, item) => {
                    const key = String(item.id);
                    const quantity = Math.max(1, Number(item.quantity) || 1);
                    acc.set(
                      key,
                      (acc.get(key) || 0) + quantity
                    );
                    return acc;
                  }, new Map())
                  .entries()
              ).map(([id, quantity]) => ({
                id: Number(id),
                quantity,
                properties: { _bundle_gift: 'true' },
              }));
              if (!aggregatedGiftItems.length) return Promise.resolve(false);

              const addInBulk = () =>
                fetch('/cart/add.js', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                  },
                  body: JSON.stringify({ items: aggregatedGiftItems }),
                }).then((res) => res.ok);

              const addSequential = () =>
                aggregatedGiftItems
                .reduce((chain, item) => {
                  return chain.then(async (count) => {
                    const giftResponse = await fetch('/cart/add.js', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                      },
                      body: JSON.stringify(item),
                    });
                    return giftResponse.ok ? count + 1 : count;
                  });
                }, Promise.resolve(0))
                .then((addedCount) => addedCount > 0)
                .catch(() => false);

              return addInBulk().then((ok) => (ok ? true : addSequential()));
            };

            const refreshDrawerSections = () => {
              const hasDrawer = Boolean(document.querySelector('cart-drawer-items'));
              if (!hasDrawer) return Promise.resolve();
              return fetch(`${routes.cart_url}?section_id=cart-drawer`)
                .then((res) => res.text())
                .then((responseText) => {
                  const html = new DOMParser().parseFromString(responseText, 'text/html');
                  const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
                  selectors.forEach((selector) => {
                    const targetElement = document.querySelector(selector);
                    const sourceElement = html.querySelector(selector);
                    if (targetElement && sourceElement) targetElement.replaceWith(sourceElement);
                  });
                })
                .catch(() => {});
            };

            return addGiftItems().then((giftsAdded) => {
              const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
              if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                }).then(() => {
                  CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                });
              if (giftsAdded) {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'bundle-gifts',
                  productVariantId: formData.get('id'),
                  cartData: response,
                });
              }
              this.error = false;
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      CartPerformance.measure("add:paint-updated-sections", () => {
                        this.cart.renderContents(response);
                      });
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  this.cart.renderContents(response);
                });
              }
              return refreshDrawerSections();
            });
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      handleBundleAdd(bundleContext, evt) {
        const mainItems = Array.isArray(bundleContext.mainItems) ? bundleContext.mainItems : [];
        const giftItems = Array.isArray(bundleContext.giftItems) ? bundleContext.giftItems : [];
        if (!mainItems.length) {
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');
          return;
        }

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items: mainItems }),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description || response.message || 'Unable to add bundle');
              this.error = true;
              return;
            }
            this.error = false;
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: String(bundleContext.mainItems[0]?.id || ''),
              cartData: response,
            });

            const addGiftItem = (item, withProperties = true) => {
              const payload = { id: item.id, quantity: item.quantity };
              return fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                  'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify(payload),
              })
                .then((res) => res.json().catch(() => ({})))
                .then((data) => {
                  return data && !data.status;
                })
                .catch(() => false);
            };

            const addGifts = () => {
              if (!giftItems.length) return Promise.resolve(0);
              return giftItems
                .reduce((chain, item) => {
                  return chain.then(async (count) => {
                    const ok = await addGiftItem(item, true);
                    return ok ? count + 1 : count;
                  });
                }, Promise.resolve(0));
            };

            const ensureGiftsInCart = () => {
              const ids = Array.isArray(bundleContext.giftVariantIds) ? bundleContext.giftVariantIds : [];
              if (!ids.length) return Promise.resolve();
              return fetch('/cart.js', {
                headers: {
                  Accept: 'application/json',
                  'X-Requested-With': 'XMLHttpRequest',
                },
              })
                .then((res) => res.json())
                .then((cartState) => {
                  const qtyByVariant = new Map();
                  (cartState.items || []).forEach((line) => {
                    qtyByVariant.set(String(line.id), (qtyByVariant.get(String(line.id)) || 0) + Number(line.quantity || 0));
                  });
                  const missingGiftItems = ids
                    .filter((id) => (qtyByVariant.get(String(id)) || 0) < 1)
                    .map((id) => ({ id, quantity: 1 }));
                  if (!missingGiftItems.length) return;
                  return missingGiftItems.reduce((chain, item) => {
                    return chain.then(() => addGiftItem(item, true));
                  }, Promise.resolve());
                })
                .catch(() => {});
            };

            return addGifts()
              .then(() => ensureGiftsInCart())
              .then(() => this.renderBundleCartContents(String(bundleContext.mainItems[0]?.id || '')))
              .then(() => {
              const drawer = document.querySelector('cart-drawer');
              if (drawer && typeof drawer.open === 'function') drawer.open();
            });
          })
          .catch((e) => {
            console.error(e);
            this.error = true;
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      renderBundleCartContents(productVariantId) {
        if (!this.cart || this.cart.tagName !== 'CART-DRAWER') {
          return this.refreshDrawerSections();
        }

        const root = (window.routes && window.routes.root_url) || '/';
        const sectionUrl = `${root}${root.includes('?') ? '&' : '?'}sections=cart-drawer,cart-icon-bubble`;

        return fetch(sectionUrl, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json',
          },
        })
          .then((res) => res.json())
          .then((sections) => {
            if (!sections || !sections['cart-drawer']) return this.refreshDrawerSections();
            this.cart.renderContents({
              id: productVariantId,
              sections,
            });
          })
          .catch(() => this.refreshDrawerSections());
      }

      refreshDrawerSections() {
        const hasDrawer = Boolean(document.querySelector('cart-drawer-items'));
        if (!hasDrawer) return Promise.resolve();
        return fetch(`${routes.cart_url}?section_id=cart-drawer`)
          .then((res) => res.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const selectors = ['cart-drawer-items', '.cart-drawer__footer', '#cart-icon-bubble'];
            selectors.forEach((selector) => {
              const targetElement = document.querySelector(selector);
              const sourceElement = html.querySelector(selector);
              if (targetElement && sourceElement) targetElement.replaceWith(sourceElement);
            });
          })
          .catch(() => {});
      }

      getBundleContext() {
        const productInfo = this.closest('product-info');
        if (!productInfo) return null;

        const selectedRadio = productInfo.querySelector('input[data-offer-radio]:checked');
        if (!selectedRadio) return null;

        const root = selectedRadio.closest('[data-bundle-offer-root]');
        const card = root ? root.querySelector('[data-offer-card]') : null;
        if (!root) return null;

        const quantity = selectedRadio.dataset.offerQty || card?.dataset.offerQty || '';
        const variantSelects = card ? Array.from(card.querySelectorAll('[data-variant-select]')) : [];
        let selectedVariantIds = variantSelects.map((select) => Number(select.value)).filter((id) => id > 0);
        if (!selectedVariantIds.length && this.form.dataset.bundleVariantItems) {
          try {
            const parsedItems = JSON.parse(this.form.dataset.bundleVariantItems);
            if (Array.isArray(parsedItems)) {
              selectedVariantIds = parsedItems
                .filter((item) => Number(item?.id) > 0 && Number(item?.quantity) > 0)
                .flatMap((item) => Array(Number(item.quantity)).fill(Number(item.id)));
            }
          } catch (_) {}
        }
        const variantId = selectedVariantIds[0] ? String(selectedVariantIds[0]) : '';
        const mainItemsMap = selectedVariantIds.reduce((acc, id) => {
          acc.set(String(id), (acc.get(String(id)) || 0) + 1);
          return acc;
        }, new Map());
        const mainItems = Array.from(mainItemsMap.entries()).map(([id, qty]) => ({
          id: Number(id),
          quantity: Number(qty),
        }));

        const giftItemsRaw = [
          Number(root.dataset.gift1VariantId || 0),
          Number(root.dataset.gift2VariantId || 0),
          Number(root.dataset.gift3VariantId || 0),
        ].filter((id) => id > 0);
        let fallbackGiftItemsRaw = [];
        if (!giftItemsRaw.length && this.form?.dataset?.bundleGiftItems) {
          try {
            const parsedGiftItems = JSON.parse(this.form.dataset.bundleGiftItems);
            if (Array.isArray(parsedGiftItems)) {
              fallbackGiftItemsRaw = parsedGiftItems
                .map((item) => Number(item?.id || 0))
                .filter((id) => id > 0);
            }
          } catch (_) {}
        }
        const finalGiftIds = giftItemsRaw.length ? giftItemsRaw : fallbackGiftItemsRaw;
        const giftItems = finalGiftIds.map((id) => ({
          id,
          quantity: 1,
        }));

        return {
          isBundleOffer: true,
          quantity: String(quantity || ''),
          variantId: String(variantId || ''),
          selectedVariantIds,
          mainItems,
          giftVariantIds: finalGiftIds,
          giftItems,
        };
      }
    }
  );
}